if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.quantityInput = this.querySelector('.quantity__input');
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.abortController?.abort();
        this.quantityRulesController?.abort();
        this.onVariantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        if (this.swapUtilityInitialized) return;
        this.swapUtilityInitialized = true;
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, selectedVariantId, selectedVariant } }) {
        if (!this.contains(event.target)) return;

        window.CartDebug?.log('variant.change', { section: this.dataset.section, selectedVariantId });
        this.resetProductFormState();
        const subscriptionWidget = this.querySelector('subscription-widget');
        subscriptionWidget?.beginVariantUpdate?.();

        if (selectedVariantId) {
          this.updateVariantInputs(selectedVariantId);
          this.updateVisiblePrice(selectedVariant);
          this.productForm?.toggleSubmitButton(!selectedVariant?.available || Boolean(subscriptionWidget), window.variantStrings.soldOut);
        }

        const expectedOptionCount = Number.parseInt(this.variantSelectors?.dataset.optionCount || '0', 10);
        if (
          !selectedVariantId &&
          (selectedOptionValues.length === 0 ||
            (expectedOptionCount > 0 && selectedOptionValues.length !== expectedOptionCount))
        ) {
          const error = new Error(
            `Variant selection is incomplete: expected ${expectedOptionCount || 'at least one'} selected option, received ${selectedOptionValues.length}.`
          );
          console.error(error);
          this.variantSelectors?.rejectPendingSelectPromise(error);
          this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);
          return;
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
            selectedVariantId
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl, selectedVariant),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const sourceProductInfo = html.querySelector(selector);
          const variant = this.getSelectedVariant(sourceProductInfo);
          this.variantSelectors?.resolvePendingSelectPromise(variant, this.getVariantSelects(sourceProductInfo));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              this.scopeRenderedNode(html.querySelector('product-info')),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        const controller = this.abortController;

        let timedOut = false;
        const startedAt = Date.now();
        const log = (stage, data = {}) => window.CartDebug?.log(stage, { section: this.dataset.section, startedAt, ...data });
        const timer = window.setTimeout(() => { timedOut = true; controller.abort(); }, 15000);
        log('variant.request');
        return fetch(requestUrl, { signal: controller.signal })
          .then((response) => {
            log('variant.response', { status: response.status, elapsedMs: Date.now() - startedAt });
            if (!response.ok) {
              throw new Error(`Variant section request failed with status ${response.status}.`);
            }
            return response.text();
          })
          .then((responseText) => {
            if (controller.signal.aborted || this.abortController !== controller) return;
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            if (!this.isConnected) return;
            callback(html);
            log('variant.ready', { disabled: this.productForm?.submitButton?.disabled, subscriptionPending: this.querySelector('subscription-widget')?.dataset.variantPending });
            // set focus to last clicked option value
            if (targetId) Array.from(this.querySelectorAll('[id]')).find((node) => node.id === targetId)?.focus();
          })
          .catch((error) => {
            if (this.abortController !== controller || (controller.signal.aborted && !timedOut)) {
              log('variant.cancelled');
              return;
            }
            this.pendingRequestUrl = null;
            log('variant.error', { code: timedOut ? 'TIMEOUT' : error.name, elapsedMs: Date.now() - startedAt });
            if (timedOut) {
              this.productForm?.handleErrorMessage('Purchase options took too long to update. Please select the option again or reload the product page.');
            } else if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
              this.productForm?.handleErrorMessage('Purchase options could not be updated. Please select the option again or reload the product page.');
            }
            this.variantSelectors?.rejectPendingSelectPromise(error);
          })
          .finally(() => window.clearTimeout(timer));
      }

      parseJsonScript(parent, selector) {
        const script = parent?.querySelector(selector);
        if (!script) {
          console.error(`Variant response is missing required selector: ${selector}`);
          return null;
        }
        try {
          return JSON.parse(script.textContent);
        } catch (error) {
          console.error(`Variant response contains invalid JSON for selector: ${selector}`, error);
          return null;
        }
      }

      getVariantSelects(queryRoot) {
        return queryRoot?.querySelector('variant-selects');
      }

      getSelectedVariant(productInfoNode) {
        const variantSelects = this.getVariantSelects(productInfoNode);
        const variants = this.parseJsonScript(variantSelects, '[data-product-variants]');
        if (!Array.isArray(variants)) return null;

        const selectedVariantId = variantSelects?.dataset.selectedVariantId;
        if (selectedVariantId) {
          const selectedById = variants.find((variant) => String(variant.id) === String(selectedVariantId));
          if (selectedById) return selectedById;
        }

        const selectedValues = Array.from(
          variantSelects?.querySelectorAll('select option:checked, input[type="radio"]:checked') || []
        ).map(({ value }) => String(value));

        return variants.find(
          (variant) =>
            Array.isArray(variant.options) &&
            variant.options.length === selectedValues.length &&
            variant.options.every((value, index) => String(value) === selectedValues[index])
        ) || null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false, selectedVariantId = null) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (selectedVariantId) {
          params.push(`variant=${encodeURIComponent(selectedVariantId)}`);
        } else if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html, selectedVariant = null) {
        if (window.Shopify?.designMode) return;
        const variantSelects = this.scopeRenderedNode(html.querySelector('variant-selects'));
        if (variantSelects) {
          // Section rendering can return markup with the previous option still
          // checked. Apply the customer's current variant before inserting the
          // replacement picker so the UI and submitted variant stay aligned.
          if (selectedVariant?.options?.length) {
            variantSelects.dataset.selectedVariantId = String(selectedVariant.id);
            if (selectedVariant.price != null) {
              variantSelects.dataset.selectedPriceAmount = String(selectedVariant.price / 100);
            }
            variantSelects.querySelectorAll('.product-form__input').forEach((group, index) => {
              const value = String(selectedVariant.options[index] ?? '');
              const select = group.querySelector('select');
              if (select) {
                select.value = value;
                Array.from(select.options).forEach((option) => {
                  option.toggleAttribute('selected', option.value === value);
                });
              }
              group.querySelectorAll('input[type="radio"]').forEach((input) => {
                input.checked = input.value === value;
                input.toggleAttribute('checked', input.checked);
              });
            });
          }
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      scopeRenderedNode(source) {
        if (!source || !this.dataset.originalSection) return source;
        const clone = source.cloneNode(true);
        const sourceSection = this.sectionId;
        const section = this.dataset.section;
        const references = new Set(['id', 'for', 'form', 'name', 'list', 'headers', 'aria-labelledby', 'aria-describedby', 'aria-controls', 'aria-owns', 'aria-activedescendant', 'data-section', 'data-section-id', 'data-media-id', 'data-target', 'data-modal', 'data-id']);
        // Rescope DOM references only. Product/variant JSON and customer text
        // are data, not template identifiers, and must not be rewritten.
        [clone, ...clone.querySelectorAll('*')].forEach((node) => {
          Array.from(node.attributes).forEach(({ name, value }) => {
            if ((references.has(name) || (name === 'href' && value.startsWith('#'))) && value.includes(sourceSection)) {
              node.setAttribute(name, value.split(sourceSection).join(section));
            }
          });
          if (node.tagName === 'STYLE') node.textContent = node.textContent.split(sourceSection).join(section);
        });
        const owners = clone.matches('product-info, variant-selects, variant-radios') ? [clone] : [];
        owners.push(...clone.querySelectorAll('product-info, variant-selects, variant-radios'));
        owners.forEach((node) => {
          node.dataset.originalSection = sourceSection;
          node.dataset.updateUrl = 'false';
        });
        return clone;
      }

      handleUpdateProductInfo(productUrl, requestedVariant = null) {
        return (html) => {
          const sourceVariantSelects = this.getVariantSelects(html);
          // Keep the exact variant selected by the customer authoritative. A
          // section response can briefly render the previous variant in the
          // theme editor, which otherwise resets radio buttons and submits the
          // wrong variant.
          const variant = requestedVariant || this.getSelectedVariant(html);

          // Resolve product:select promise before updateOptionValues replaces the variant-selects DOM element
          this.variantSelectors?.resolvePendingSelectPromise(variant, sourceVariantSelects);

          this.updatePickupAvailability(variant);
          this.updateOptionValues(html, variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false, required = false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = this.scopeRenderedNode(source).innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
              return true;
            }
            if (required) console.error(`Variant update target is missing: ${id}-${this.sectionId}`);
            return false;
          };

          updateSourceFromDestination('price', (source) => false, true);
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('ConversionActivity', ({ innerText }) => innerText.trim() === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          // Reapply local price data after copying the section response. This
          // covers the same stale-response case as the option picker above.
          this.updateVisiblePrice(variant);
          this.updateTrendPanels(html);
          const subscriptionReady = this.querySelector('subscription-widget')?.refreshFromProduct?.(html, variant, this.sectionId) ?? true;

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            !subscriptionReady || (html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true),
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateTrendPanels(html) {
        const destinations = [...this.querySelectorAll('[data-trend-panel-block]')];
        html.querySelectorAll('[data-trend-panel-block]').forEach((source) => {
          const destination = destinations.find(
            (candidate) => candidate.dataset.trendPanelBlock === source.dataset.trendPanelBlock
          );
          if (destination) destination.replaceWith(source.cloneNode(true));
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) {
            console.error(`Variant ID input is missing in form: ${productForm.id}`);
            return;
          }
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      formatVariantMoney(cents) {
        const currency =
          this.variantSelectors?.dataset.currencyCode ||
          window.Shopify?.currency?.active ||
          'USD';
        const locale = document.documentElement.lang || navigator.language || 'en-US';

        try {
          return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
          }).format(Number(cents || 0) / 100);
        } catch (error) {
          console.warn('Variant price could not use the active currency formatter.', error);
          return `$${(Number(cents || 0) / 100).toFixed(2)}`;
        }
      }

      updateVisiblePrice(variant) {
        if (!variant || variant.price == null) return;

        const priceRoot = this.querySelector(`#price-${this.dataset.section}`);
        const priceContainer = priceRoot?.querySelector('.price');
        if (!priceContainer) return;

        const price = Number(variant.price || 0);
        const compareAtPrice = Number(variant.compare_at_price || 0);
        const isOnSale = compareAtPrice > price;
        const formattedPrice = this.formatVariantMoney(price);
        const formattedCompareAtPrice = isOnSale ? this.formatVariantMoney(compareAtPrice) : '';

        priceContainer.classList.toggle('price--on-sale', isOnSale);
        priceContainer.classList.toggle('price--sold-out', variant.available === false);

        priceContainer
          .querySelectorAll('.price__regular .price-item--regular, .price__sale .price-item--sale')
          .forEach((node) => {
            node.textContent = formattedPrice;
          });

        priceContainer.querySelectorAll('.price__compare-price .price-item--regular').forEach((node) => {
          node.textContent = formattedCompareAtPrice;
        });

        const percentageSaved = priceContainer.querySelector('.badge__percentage-saved');
        if (percentageSaved) {
          percentageSaved.textContent = isOnSale ? String(Math.floor(((compareAtPrice - price) * 100) / compareAtPrice)) : '0';
        }

        const moneySaved = priceContainer.querySelector('.badge__money-saved');
        if (moneySaved) {
          moneySaved.textContent = isOnSale ? this.formatVariantMoney(compareAtPrice - price) : '';
        }

        this.querySelectorAll('[id^="main-atc-price-"]').forEach((node) => {
          node.textContent = formattedPrice;
          node.classList.toggle('hidden', variant.available === false);
        });
      }

      updatePickupAvailability(variant) {
        const pickupAvailability = this.pickupAvailability;
        if (!pickupAvailability) return;

        try {
          if (typeof pickupAvailability.update === 'function') {
            pickupAvailability.update(variant);
          } else if (variant?.id && variant.available && typeof pickupAvailability.fetchAvailability === 'function') {
            pickupAvailability.dataset.variantId = String(variant.id);
            pickupAvailability.fetchAvailability(variant.id);
          } else if (!variant?.available) {
            pickupAvailability.innerHTML = '';
            pickupAvailability.removeAttribute('available');
          }
        } catch (error) {
          console.warn('Pickup availability could not be refreshed.', error);
        }
      }

      updateURL(url, variantId) {
        const shopOrigin = window.shopUrl || window.location.origin;
        const shareUrl = `${shopOrigin}${url}${variantId ? `?variant=${variantId}` : ''}`;
        const shareButton = this.querySelector('share-button');
        if (typeof shareButton?.updateUrl === 'function') {
          shareButton.updateUrl(shareUrl);
        } else if (shareButton) {
          shareButton.urlToShare = shareUrl;
          const shareInput = shareButton.querySelector('input');
          if (shareInput) shareInput.value = shareUrl;
        }

        if (this.dataset.updateUrl === 'false' || this.closest('quick-add-modal')) return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        this.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const gallery = document.getElementById(`MediaGallery-${this.dataset.section}`)
          || this.querySelector('media-gallery');
        const mediaGallerySource = gallery?.querySelector('ul');
        const mediaGalleryDestination = html.getElementById(`MediaGallery-${this.dataset.section}`)?.querySelector('ul')
          || html.querySelector('media-gallery ul');

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        gallery?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        const spinner = this.querySelector('.quantity__rules-cart .loading__spinner');
        spinner?.classList.remove('hidden');
        this.quantityRulesController?.abort();
        this.quantityRulesController = new AbortController();
        const controller = this.quantityRulesController;
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`, { signal: controller.signal })
          .then((response) => response.text())
          .then((responseText) => {
            if (!this.isConnected || controller.signal.aborted || this.productForm?.variantIdInput?.value !== currentVariantId) return;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => { if (e.name !== 'AbortError') console.error(e); })
          .finally(() => { if (this.quantityRulesController === controller) spinner?.classList.add('hidden'); });
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput || !this.quantityForm) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        if (!quantityFormUpdated) return;
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}
